/**
 * Universal tracking tags — dash data-layer client (feature #161).
 *
 * Thin typed wrapper around `apiClient` for the `/api/v1/universal-tag`
 * surface. Mirrors the contract in `apps/api/src/routes/universal-tag.ts`:
 *
 *   POST   /                                 { name, events?, campaignId? }
 *                                            → View
 *   GET    /                                 → { tags: View[] }
 *   GET    /:id                              → View
 *   PUT    /:id                              { name?, events?, campaignId?,
 *                                              enabled? } → View
 *   DELETE /:id                              → { ok: true, id }
 *   GET    /:id/events?limit=N               → { tag_id, limit, events[] }
 *   GET    /t/:tag_key                       (public fire endpoint — not
 *                                             called from the dashboard)
 *
 * Keep this module dependency-free beyond `apiClient` — the page component
 * and the unit tests both import from here, and the smoke harness reuses
 * the pure helpers (`draftSchema`, `buildTagUrl`) without pulling React.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Row + event shapes — match the API's UniversalTagView /
// UniversalTagEventView.
// ---------------------------------------------------------------------------

export const UNIVERSAL_TAG_EVENTS = [
	"click",
	"impression",
	"conversion",
] as const;

export type UniversalTagEventKind = (typeof UNIVERSAL_TAG_EVENTS)[number];

export const EVENT_LABELS: Record<UniversalTagEventKind, string> = {
	click: "Click",
	impression: "Impression",
	conversion: "Conversion",
};

export const EVENT_DESCRIPTIONS: Record<UniversalTagEventKind, string> = {
	click: "Fires on user click-throughs. Redirects to the ref URL when present.",
	impression:
		"Fires on ad render. Returns a 1x1 pixel, safe to drop into banners.",
	conversion:
		"Fires on post-purchase / signup. Accepts a numeric value + currency.",
};

export interface UniversalTagView {
	id: string;
	tag_key: string;
	name: string;
	campaign_id: string | null;
	events: UniversalTagEventKind[];
	enabled: boolean;
	created_at: number;
	updated_at: number;
}

export interface UniversalTagEventView {
	id: string;
	tag_id: string;
	event: UniversalTagEventKind;
	ref: string | null;
	value: number | null;
	currency: string | null;
	fired_at: number;
}

export interface ListTagsResponse {
	tags: UniversalTagView[];
}

export interface ListEventsResponse {
	tag_id: string;
	limit: number;
	events: UniversalTagEventView[];
}

// ---------------------------------------------------------------------------
// Create / patch draft + hand-rolled safeParse surface.
// ---------------------------------------------------------------------------

export interface TagDraft {
	name: string;
	events: UniversalTagEventKind[];
	campaignId: string;
}

export const TAG_NAME_MAX = 128;
export const CAMPAIGN_ID_MAX = 128;

export const EMPTY_DRAFT: TagDraft = {
	name: "",
	events: ["click", "impression", "conversion"],
	campaignId: "",
};

export interface ValidatedTagDraft {
	name: string;
	events: UniversalTagEventKind[];
	campaignId?: string;
}

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export const tagDraftSchema = {
	safeParse(input: TagDraft): ParseResult<ValidatedTagDraft> {
		const errors: Record<string, string> = {};

		const name = (input.name ?? "").trim();
		if (name.length === 0) {
			errors.name = "Name is required.";
		} else if (name.length > TAG_NAME_MAX) {
			errors.name = `Name must be ${TAG_NAME_MAX} characters or fewer.`;
		}

		const events = Array.isArray(input.events)
			? input.events.filter((e): e is UniversalTagEventKind =>
					(UNIVERSAL_TAG_EVENTS as readonly string[]).includes(e),
				)
			: [];
		if (events.length === 0) {
			errors.events = "Select at least one event to track.";
		}

		const campaignId = (input.campaignId ?? "").trim();
		if (campaignId.length > CAMPAIGN_ID_MAX) {
			errors.campaignId = `Campaign ID must be ${CAMPAIGN_ID_MAX} characters or fewer.`;
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				name,
				events,
				...(campaignId.length > 0 ? { campaignId } : {}),
			},
		};
	},
};

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

export function formatFiredAt(seconds: number | null | undefined): string {
	if (seconds === null || seconds === undefined || Number.isNaN(seconds))
		return "—";
	const d = new Date(seconds * 1000);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toISOString().replace("T", " ").replace(/\.\d{3}Z$/, "Z");
}

export function eventBadgeClass(event: UniversalTagEventKind): string {
	switch (event) {
		case "click":
			return "bg-sky-50 text-sky-700 border-sky-200";
		case "impression":
			return "bg-zinc-50 text-zinc-700 border-zinc-200";
		case "conversion":
			return "bg-emerald-50 text-emerald-700 border-emerald-200";
	}
}

export function sortTags(
	tags: readonly UniversalTagView[],
): UniversalTagView[] {
	return tags
		.slice()
		.sort((a, b) => b.created_at - a.created_at || b.id.localeCompare(a.id));
}

/**
 * Canonicalize the event-set for display so users always see a stable order
 * regardless of how the server encoded it.
 */
export function canonicalEventOrder(
	events: readonly UniversalTagEventKind[],
): UniversalTagEventKind[] {
	return UNIVERSAL_TAG_EVENTS.filter((e) => events.includes(e));
}

/**
 * Build the public firing URL for an embed. Callers pass the dashboard's
 * configured API base (so the URL is usable from marketing pages rather
 * than pointing at localhost during dev).
 */
export function buildTagUrl(
	base: string,
	tagKey: string,
	event: UniversalTagEventKind,
	extras: { ref?: string; value?: number; currency?: string } = {},
): string {
	const trimmedBase = (base ?? "").replace(/\/+$/, "");
	const url = new URL(`${trimmedBase}/api/v1/universal-tag/t/${encodeURIComponent(tagKey)}`);
	url.searchParams.set("event", event);
	if (extras.ref) url.searchParams.set("ref", extras.ref);
	if (typeof extras.value === "number" && Number.isFinite(extras.value)) {
		url.searchParams.set("value", String(extras.value));
	}
	if (extras.currency) url.searchParams.set("currency", extras.currency);
	return url.toString();
}

// ---------------------------------------------------------------------------
// Snippet builders — what the operator copy-pastes.
// ---------------------------------------------------------------------------

export function impressionSnippet(base: string, tagKey: string): string {
	return `<img src="${buildTagUrl(base, tagKey, "impression")}" width="1" height="1" alt="" style="display:none" />`;
}

export function clickSnippet(base: string, tagKey: string): string {
	const url = buildTagUrl(base, tagKey, "click", { ref: "{{DESTINATION_URL}}" });
	return `<a href="${url}">Track click</a>`;
}

export function conversionSnippet(base: string, tagKey: string): string {
	const url = buildTagUrl(base, tagKey, "conversion", {
		value: 0,
		currency: "USD",
	});
	return `<script>
(function(){
  var url = '${url}'.replace('value=0','value=' + encodeURIComponent(ORDER_TOTAL));
  new Image().src = url;
})();
</script>`;
}

// ---------------------------------------------------------------------------
// Remote operations
// ---------------------------------------------------------------------------

const BASE = "/universal-tag";

export async function createTag(payload: ValidatedTagDraft): Promise<UniversalTagView> {
	const body: Record<string, unknown> = {
		name: payload.name,
		events: payload.events,
	};
	if (payload.campaignId) body.campaignId = payload.campaignId;
	return await apiClient.post<UniversalTagView>(BASE, body);
}

export async function listTags(): Promise<UniversalTagView[]> {
	try {
		const res = await apiClient.get<ListTagsResponse>(BASE);
		return res.tags ?? [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

export async function getTag(id: string): Promise<UniversalTagView | null> {
	try {
		return await apiClient.get<UniversalTagView>(`${BASE}/${encodeURIComponent(id)}`);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return null;
		throw err;
	}
}

export interface UpdateTagPayload {
	name?: string;
	events?: UniversalTagEventKind[];
	campaignId?: string | null;
	enabled?: boolean;
}

export async function updateTag(
	id: string,
	patch: UpdateTagPayload,
): Promise<UniversalTagView> {
	return await apiClient.put<UniversalTagView>(
		`${BASE}/${encodeURIComponent(id)}`,
		patch,
	);
}

export async function deleteTag(id: string): Promise<void> {
	try {
		await apiClient.delete(`${BASE}/${encodeURIComponent(id)}`);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return;
		throw err;
	}
}

export async function listEvents(
	tagId: string,
	limit = 50,
): Promise<UniversalTagEventView[]> {
	try {
		const res = await apiClient.get<ListEventsResponse>(
			`${BASE}/${encodeURIComponent(tagId)}/events`,
			{ query: { limit } },
		);
		return res.events ?? [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

// ---------------------------------------------------------------------------
// TanStack Query keys
// ---------------------------------------------------------------------------

export const universalTagQueryKeys = {
	all: ["universal-tag"] as const,
	list: () => [...universalTagQueryKeys.all, "list"] as const,
	detail: (id: string) => [...universalTagQueryKeys.all, "detail", id] as const,
	events: (id: string) => [...universalTagQueryKeys.all, "events", id] as const,
};

// ---------------------------------------------------------------------------
// Cache merge helper — swap a single updated tag into a cached list.
// ---------------------------------------------------------------------------

export function replaceInList(
	tags: readonly UniversalTagView[],
	next: UniversalTagView,
): UniversalTagView[] {
	let found = false;
	const out = tags.map((t) => {
		if (t.id === next.id) {
			found = true;
			return next;
		}
		return t;
	});
	if (!found) out.unshift(next);
	return out;
}
